import { FeatureFlagIdAccessConflicts, FeatureFlagIdAccessRequest } from 'c1gen/features'
import { useFeatureChecker } from 'components/no-ui/capability'
import { DashboardContext, DashboardContextRecord, UserContext } from 'lib/context/app'
import {
    GetAccessOverviewRequest,
    GetAccessOverviewResponse,
    GetConflictsOverviewResponse,
    GetTasksOverviewRequest,
    GetTasksOverviewResponse,
} from 'pbts/c1/api/dashboard/v1/private/dashboard'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { getConflictsOverviewApi, getMyAccessCountApi, getMyTasksCountApi } from 'redux_toolkit/access-reviews'

const updateDashboardContextFromTaskCount = (
    dashboardContext: DashboardContextRecord,
    tasks: GetTasksOverviewResponse,
): DashboardContextRecord => {
    return dashboardContext
        .set('request_tasks', tasks.grantTasks?.count || 0)
        .set('review_tasks', tasks.certifyTasks?.count || 0)
        .set('revoke_tasks', tasks.revokeTasks?.count || 0)
        .set('enrollment_tasks', tasks.enrollmentTasks?.count || 0)
}

const updateDashboardContextFromAccessCount = (
    dashboardContext: DashboardContextRecord,
    access: GetAccessOverviewResponse,
): DashboardContextRecord => {
    return dashboardContext
        .set('my_requests', access.requests?.count || 0)
        .set('my_expiring_access', access.grants?.countExpiring || 0)
}

const updateDashboardContextFromConflictCount = (
    dashboardContext: DashboardContextRecord,
    conflicts: GetConflictsOverviewResponse,
): DashboardContextRecord => {
    return dashboardContext
        .set('conflicts', conflicts.count || 0)
}

const DashboardContextProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
    const userContext = useContext(UserContext)
    const userId = userContext.user.id
    const hasAccessRequestFF = useFeatureChecker(FeatureFlagIdAccessRequest)
    const hasAccessConflictsFF = useFeatureChecker(FeatureFlagIdAccessConflicts)

    const {
        data: tasks,
        isError: isTasksError,
    } = getMyTasksCountApi.useGetTasksOverview(
        GetTasksOverviewRequest.create({ targetUserId: userId }),
        {
            notify: true,
        },
    )

    const {
        data: access,
        isError: isAccessError,
    } = getMyAccessCountApi.useGetAccessOverview(
        GetAccessOverviewRequest.create({ targetUserId: userId }),
        {
            actionable: !!hasAccessRequestFF,
            notify: true,
        },
    )

    const {
        data: conflicts,
        isError: isConflictsError,
    } = getConflictsOverviewApi.useGetConflictsOverview(
        GetAccessOverviewRequest.create({ targetUserId: userId }),
        {
            actionable: !!hasAccessConflictsFF,
            notify: true,
        },
    )

    const [dashboardContext, setDashboardContext] = useState<DashboardContextRecord>(new DashboardContextRecord())

    useEffect(() => {
        if (isTasksError || !tasks) {
            return
        }

        setDashboardContext((prevDashboardContext) => updateDashboardContextFromTaskCount(prevDashboardContext, tasks))
    }, [isTasksError, tasks])

    useEffect(() => {
        if (isAccessError || !access) {
            return
        }

        setDashboardContext((prevDashboardContext) =>
            updateDashboardContextFromAccessCount(prevDashboardContext, access))
    }, [access, isAccessError])

    useEffect(() => {
        if (isConflictsError || !conflicts) {
            return
        }

        setDashboardContext((prevDashboardContext) =>
            updateDashboardContextFromConflictCount(prevDashboardContext, conflicts))
    }, [conflicts, isConflictsError])

    return <DashboardContext.Provider value={dashboardContext}>{children}</DashboardContext.Provider>
}

export default DashboardContextProvider
